import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch';

import { Configure } from '@/components/Configure';
import { InstantSearchStateUpdater } from '@/components/InstantSearchUpdater/InstantSearchUpdater';
import { Spacer } from '@/components/Spacer';
import { ListingPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import {
  buildQueryParamsFromSearchUiState,
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { Default } from '@/layouts/Default';
import { getRegionFromLocale } from '@/lib/regions';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { listingActions } from '@/states/listingState';

import { usePreRenderCmsSections } from '../AllTheStrapsView/helpers';
import type { AllTheStrapsViewProps } from './types';

export const AllTheStrapsView = ({
  algoliaFilters,
  algoliaUserToken,
  allTheStrapsCmsData,
  draftMode,
  instantSearchProps,
  serverState,
}: AllTheStrapsViewProps) => {
  const liveAllTheStrapsCmsData = useContentfulLiveUpdates(
    allTheStrapsCmsData,
    { query: ListingPageDocument },
  );
  const { cmsSectionAll } = usePreRenderCmsSections(liveAllTheStrapsCmsData);
  const { asPath, locale, push, query } = useRouter();
  const pageIndex = query?.page && parseInt(query.page as string);
  const indexName = getIndexName('PRODUCT', locale);
  const lastUiState = useRef<SearchUiState | null>(null);

  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    return buildUiStateFromURL(
      asPath,
      indexName,
      undefined,
      undefined,
      region.locale,
      serverState,
      'EN_US',
    );
  }, [asPath, indexName, locale, serverState]);

  const syncStateWithUrl = useCallback(
    (uiState: SearchUiState) => {
      const queryParams = buildQueryParamsFromSearchUiState(
        uiState,
        indexName,
        query,
        serverState,
        'EN_US',
      );

      const cleanPathname = asPath.split('?')[0];
      push({ pathname: cleanPathname, query: queryParams }, undefined, {
        shallow: true,
      });
    },
    [indexName, push, query, serverState],
  );

  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_STRAPS,
  });

  useEffect(() => {
    listingActions.updateListingType('STRAP');
  }, []);

  // Destructure to remove initialUiState and serverState from instantSearchProps
  // to prevent them from overriding our translated uiStateFromURL and serverState prop
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialUiState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serverState: _serverStateFromProps,
    ...restInstantSearchProps
  } = (instantSearchProps as any) || {};

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName={indexName}
        initialUiState={uiStateFromURL}
        insights={false}
        onStateChange={({ setUiState, uiState }) => {
          if (!isEqual(uiState, lastUiState.current)) {
            lastUiState.current = uiState;
            setUiState(uiState);
            syncStateWithUrl(uiState);
          }
        }}
        searchClient={algoliaSearchClient}
        {...restInstantSearchProps}
      >
        <InstantSearchStateUpdater
          key={asPath}
          expectedUiState={uiStateFromURL}
        />
        <Configure
          analyticsTags={[
            'platform:web',
            'product:straps',
            'page:listing',
            'context:all-straps',
          ]}
          clickAnalytics={true}
          distinct={true}
          facetingAfterDistinct={true}
          filters={algoliaFilters}
          userToken={algoliaUserToken}
          {...(typeof pageIndex === 'number' && pageIndex > 0
            ? { page: pageIndex - 1 }
            : {})}
        />
        {cmsSectionAll}
        <Spacer height={32} />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

AllTheStrapsView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <Default
      cmsData={page.props.cmsDefaultLayoutData}
      draftMode={page.props.draftMode}
    >
      {page}
    </Default>
  </DraftModeContext.Provider>
);
